// HIÁNYZÓ ÖSSZEG PÓTLÁSA — az EGYSZERŰSÍTETT NAV-számla nettójának és áfájának kézi beírása.
//
// MIÉRT LÉTEZIK: a NAV digestje az egyszerűsített számla összegét NEM közli (útiköltség, postaköltség —
// minden hónapban visszatérő tételek). A bizonylat besorolható, de az összege hiányzik, tehát a havi
// kiadás-összeg LÁTHATATLANUL alul van becsülve: az összegen nem látszik, csak ha valaki a listát böngészi.
//
// MIÉRT NETTÓ + ÁFA, ÉS NEM BRUTTÓ: a kézi kiadás-felvitel MA is így megy — a felület SZÁMOLJA a bruttót,
// nincs harmadik, kézzel írt szám. Egy második beviteli mód ugyanarra a dologra fölösleges fogalmi teher
// lenne, és a `net + áfa === bruttó` szerződés-fal is ugyanaz mindkét úton. (A bizonylaton jellemzően a
// bruttó áll — a bontást tehát a felvivő végzi el, pontosan úgy, ahogy egy kézi kiadásnál is.)
//
// A DIALÓGUS NEM ENGED MENTENI üres/érvénytelen mezővel: a szerver 400-zal utasítaná el, és a felhasználó
// egy technikai hibaüzenetet kapna arra, amit a felületen is meg lehet előzni.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { addDecimals, normalizeDecimal } from '@atlas/shared';

/** Nem-negatív, legfeljebb 2 tizedes decimális — a szerződés `decimalStr`-jének tükre. */
const DECIMAL_RE = /^\d+([.,]\d{1,2})?$/;

/** A beírt alak normalizálása (vesszős tizedes is megy — magyar billentyűzeten az a természetes). */
function toDecimal(raw: string): string | null {
  const t = raw.trim();
  if (!DECIMAL_RE.test(t)) return null;
  return normalizeDecimal(t.replace(',', '.'));
}

export interface AmountFillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A cél-sor megjelenítendő adatai (a szállító a fejlécben, a deviza a mezők mellett). */
  /** A `invoiceNumber` `null` → nincs bizonylat; ilyenkor a fejlécben csak a szállító áll. */
  target: { supplierName: string; invoiceNumber: string | null; currency: string } | null;
  /**
   * A MOSTANI összegek, ha a soron MÁR van kézi kiegészítés — ilyenkor a modál MÓDOSÍT, nem pótol, és a
   * mezők a meglévő értékkel nyílnak. `null` → üres mezők (első pótlás). Vakon felülírni egy korábbi
   * bevitelt rosszabb, mint be sem engedni a szerkesztést.
   */
  initial?: { netAmount: string; vatAmount: string } | null;
  pending: boolean;
  onSubmit: (amounts: { netAmount: string; vatAmount: string; grossAmount: string }) => void;
}

export function AmountFillDialog({
  open,
  onOpenChange,
  target,
  initial = null,
  pending,
  onSubmit,
}: AmountFillDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [net, setNet] = useState('');
  const [vat, setVat] = useState('');

  // NYITÁSKOR a mezők a CÉL-SOR állapotát veszik fel: üresen az első pótlásnál, a meglévő értékekkel a
  // módosításnál. A reset az `open`-re fut, nem a cél-sorra — a dialógus más sorra is nyílhat, és egy
  // ottfelejtett érték ROSSZ bizonylatra kerülne be.
  useEffect(() => {
    if (open) {
      setNet(initial?.netAmount ?? '');
      setVat(initial?.vatAmount ?? '');
    }
    // A `initial` szándékosan NEM függőség: a nyitás pillanatában érvényes érték számít, egy menet közbeni
    // lista-frissítés nem írhatja felül, amit a felhasználó épp gépel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const netD = toDecimal(net);
  const vatD = toDecimal(vat);
  const grossD = netD !== null && vatD !== null ? addDecimals(netD, vatD) : null;
  const valid = netD !== null && vatD !== null && grossD !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {initial === null ? t('expenses.amountFillTitle') : t('expenses.amountEditTitle')}
          </DialogTitle>
          <DialogDescription>{t('expenses.amountFillDesc')}</DialogDescription>
        </DialogHeader>

        {/* A TÖRZS a közös `DialogBody`-ban él (`px-6 py-4`), mint a többi modálban: a `DialogContent`
            SZÁNDÉKOSAN `p-0`, mert a fejléc/törzs/lábléc maga hozza a saját paddingját, és a KÖZÉP az,
            ami görget. Enélkül a tartalom kiér a panel széléig. */}
        <DialogBody className="space-y-4">
          {target !== null && (
            <Text variant="secondary" as="div" className="text-xs">
              {target.invoiceNumber === null
                ? target.supplierName
                : `${target.supplierName} · ${target.invoiceNumber}`}
            </Text>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1">
              <Text variant="secondary" as="span" className="text-xs">
                {t('expenses.amountNet')} ({target?.currency ?? ''})
              </Text>
              <Input
                value={net}
                onChange={(e) => setNet(e.target.value)}
                inputMode="decimal"
                autoFocus
                aria-label={t('expenses.amountNet')}
                className="tabular-nums"
              />
            </label>
            <label className="space-y-1">
              <Text variant="secondary" as="span" className="text-xs">
                {t('expenses.amountVat')} ({target?.currency ?? ''})
              </Text>
              <Input
                value={vat}
                onChange={(e) => setVat(e.target.value)}
                inputMode="decimal"
                aria-label={t('expenses.amountVat')}
                className="tabular-nums"
              />
            </label>
          </div>

          {/* A BRUTTÓ SZÁMOLT, nem beírható — ez a „nincs harmadik, kézzel írt szám" szabály a felületen. */}
          <Text variant="secondary" as="div" className="text-xs tabular-nums">
            {t('expenses.amountGross')}: {grossD ?? '—'} {target?.currency ?? ''}
          </Text>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!valid || pending}
            onClick={() => {
              if (netD === null || vatD === null || grossD === null) return;
              onSubmit({ netAmount: netD, vatAmount: vatD, grossAmount: grossD });
            }}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
